import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import * as Sentry from '@sentry/react'
import UAParser from 'ua-parser-js'

import {
  ZOOM_EVENTS,
  ZOOM_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { IO_DEVICES } from '@lyrahealth-inc/shared-app-logic/src/features/videoSession/constants'

import {
  Camera,
  DeviceSettings,
  JoinSessionParams,
  LeaveSessionParams,
  NETWORK_QUALITY_MAP,
  Participant,
  RecordingFetchMode,
  VideoCallEffect,
  ZoomCommand,
  ZoomNetworkQuality,
  ZoomPlatformSpecificFunction,
} from './types'
import { useZoomStream } from './useZoomStream'
import { useZoomToast, ZoomToast } from './useZoomToast'
import {
  getErrorObject,
  getVideoEffectURL,
  isIgnorableZoomError,
  isVirtualBackgroundStartError,
  VB_BROWSERS,
} from './utils'
import ZoomVideo, {
  AudioChangeAction,
  ConnectionChangePayload,
  ConnectionState,
  LeaveAudioSource,
  LocalVideoTrack,
  MobileVideoFacingMode,
  MutedSource,
  ReconnectReason,
  VideoCapturingState,
  VideoQuality,
} from './ZoomVideo'
import { AppContext } from '../../context'
import { useGetIsMobileWebBrowser } from '../useGetIsMobileWebBrowser'

const LEAVE_DELAY_ON_END_COMMAND_SEND_MS = 500

export const useZoomPlatformSpecific: ZoomPlatformSpecificFunction = ({
  sessionEndedCallback,
  sessionClosedCallback,
  addToast,
  attached,
  setNetworkQuality,
  logger,
  isRecording,
  setIsRecording,
  config,
}) => {
  const client = useMemo(() => ZoomVideo.createClient(), [])
  const stream = useZoomStream()
  const [muted, setMuted] = useState(true)
  const [sessionVideoOff, setSessionVideoOff] = useState(true)
  const [localVideoOff, setLocalVideoOff] = useState(true)
  const [cameraList, setCameraList] = useState<Camera[]>([])
  const [remoteParticipants, setRemoteParticipants] = useState<Participant[]>([])
  const [inSession, setInSession] = useState(false)
  const [selfElement, setSelfElement] = useState<HTMLElement | null>(null)
  const [sharingUserId, setSharingUserId] = useState<number | null>(null)
  const [screenshareElement, setScreenshareElement] = useState<HTMLCanvasElement | HTMLVideoElement | null>(null)
  const [videoEffect, setVideoEffectEnum] = useState<VideoCallEffect>(
    (localStorage.getItem(IO_DEVICES.EFFECT) as VideoCallEffect | null) || VideoCallEffect.NONE,
  )
  const [sessionStarted, setSessionStarted] = useState(false)
  // Mirror sessionStarted into a ref read by the connection-change handler.
  // The ref is updated synchronously at every setter call site so back-to-back
  // events (e.g. cypress firing start then close in the same tick) don't see
  // a stale ref before React commits the underlying useState change.
  const sessionStartedRef = useRef(sessionStarted)
  const updateSessionStarted = useCallback((next: boolean) => {
    sessionStartedRef.current = next
    setSessionStarted(next)
  }, [])
  const prevSelfElement = useRef<HTMLElement | null>(null)
  const [participantElement, setParticipantElement] = useState<HTMLElement | null>(null)
  const prevParticipantElement = useRef<HTMLElement | null>(null)
  const currentDisplayedParticipant = useRef<number | null>(null)
  const [previewVideoElement, setPreviewVideoElement] = useState<HTMLVideoElement | null>(null)
  const prevPreviewElement = useRef<HTMLVideoElement | HTMLCanvasElement | null>(null)
  const localVideoTrack = useRef<LocalVideoTrack | null>(null)
  const [participantScreenshareElement, setParticipantScreenshareElement] = useState<HTMLCanvasElement | null>(null)
  const [joinTime, setJoinTime] = useState<Date | null>(null)
  const [isOnline, setIsOnline] = useState(true)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const hasFailedOver = useRef(false)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)

  const addZoomToast = useZoomToast(addToast)

  const isMobileBrowser = useGetIsMobileWebBrowser()

  const [videoInput, setVideoInput] = useState<string | null>(localStorage.getItem(IO_DEVICES.CAMERA) || null)
  const [audioInput, setAudioInput] = useState<string | null>(localStorage.getItem(IO_DEVICES.MICROPHONE) || null)
  const [audioOutput, setAudioOutput] = useState<string | null>(localStorage.getItem(IO_DEVICES.SPEAKER) || null)
  const { trackEvent } = useContext(AppContext)

  // Cleared by an in-session toggle, so a waiting-room unmute still waiting on the
  // permission prompt can't override what the user chose in the session.
  const micCheckPendingRef = useRef(false)
  // Lets the Zoom audio event handlers check the mic state without re-subscribing
  // them on every toggle.
  const unmuteOnAudioJoinRef = useRef(false)
  const mutedRef = useRef(muted)
  useEffect(() => {
    mutedRef.current = muted
  }, [muted])

  // Diagnostics for unexplained mutes: when the device list last changed and the
  // last media failure the SDK reported.
  const lastDeviceChangeAtRef = useRef<number>()
  const lastMediaFailureRef = useRef<{ code: number; at: number }>()
  useEffect(() => {
    const onDeviceChange = () => {
      lastDeviceChangeAtRef.current = Date.now()
    }
    const onActiveMediaFailed = ({ code }: { code: number }) => {
      lastMediaFailureRef.current = { code, at: Date.now() }
    }
    client.on('device-change', onDeviceChange)
    client.on('active-media-failed', onActiveMediaFailed)
    return () => {
      client.off('device-change', onDeviceChange)
      client.off('active-media-failed', onActiveMediaFailed)
    }
  }, [client])

  const trackMicMutedAutomatically = useCallback(
    (trigger: 'join_snapshot' | 'zoom_muted_event' | 'audio_leave', source?: MutedSource | LeaveAudioSource) => {
      if (mutedRef.current) {
        return
      }
      const now = Date.now()
      const lastDeviceChangeAt = lastDeviceChangeAtRef.current
      const lastMediaFailure = lastMediaFailureRef.current
      trackEvent?.(ZOOM_EVENTS.MIC_MUTED_AUTOMATICALLY, {
        [ZOOM_PROPERTIES.MUTE_TRIGGER]: trigger,
        [ZOOM_PROPERTIES.MUTE_SOURCE]: source,
        [ZOOM_PROPERTIES.SDK_MUTED]: client.getCurrentUserInfo()?.muted,
        [ZOOM_PROPERTIES.SESSION_STARTED]: sessionStartedRef.current,
        [ZOOM_PROPERTIES.TAB_VISIBLE]: document.visibilityState === 'visible',
        [ZOOM_PROPERTIES.MS_SINCE_DEVICE_CHANGE]: lastDeviceChangeAt && now - lastDeviceChangeAt,
        [ZOOM_PROPERTIES.LAST_MEDIA_FAILURE_CODE]: lastMediaFailure?.code,
        [ZOOM_PROPERTIES.MS_SINCE_MEDIA_FAILURE]: lastMediaFailure && now - lastMediaFailure.at,
      })
    },
    [client, trackEvent],
  )

  const videoOff = useMemo(
    () => (sessionStarted ? sessionVideoOff : localVideoOff),
    [localVideoOff, sessionStarted, sessionVideoOff],
  )

  const onSessionClosedOrEndedCallback = useCallback(
    (rejoining: boolean) => {
      sessionStartedRef.current && !rejoining ? sessionEndedCallback?.() : sessionClosedCallback?.()
      setInSession(false)
      updateSessionStarted(false)
    },
    [sessionClosedCallback, sessionEndedCallback, updateSessionStarted],
  )
  // Feeds the SDK's enforceVirtualBackground init option; keep it browser-only
  // so SDK initialization is unchanged by the hardware check below.
  const browserSupportsVirtualBackground = useMemo(() => {
    const userAgent = new UAParser(window.navigator.userAgent)
    const browserName = userAgent.getResult().browser.name
    return !isMobileBrowser && browserName != null && VB_BROWSERS.includes(browserName)
  }, [isMobileBrowser])

  const supportsVirtualBackground = useMemo(() => {
    // Zoom also requires more than 2 logical cores to apply a virtual
    // background; hide the option where startVideo would reject it. An
    // unreported value is treated as capable — the startVideo fallback
    // covers any device this misses.
    const { hardwareConcurrency } = window.navigator
    const meetsHardwareRequirement = hardwareConcurrency === undefined || hardwareConcurrency > 2
    return browserSupportsVirtualBackground && meetsHardwareRequirement
  }, [browserSupportsVirtualBackground])

  useEffect(() => {
    const networkChanged = () => setIsOnline(navigator.onLine)
    window.addEventListener('online', networkChanged)
    window.addEventListener('offline', networkChanged)
    return () => {
      window.removeEventListener('online', networkChanged)
      window.removeEventListener('offline', networkChanged)
    }
  }, [])

  useEffect(() => {
    if (!inSession) {
      return
    }
    if (!isOnline) {
      if (!isReconnecting) {
        setIsReconnecting(true)
        addZoomToast(ZoomToast.RECONNECTING)
      }
    } else {
      if (isReconnecting && !hasFailedOver.current) {
        setIsReconnecting(false)
        addZoomToast(ZoomToast.RECONNECTED)
      }
    }
  }, [addZoomToast, inSession, isOnline, isReconnecting])

  const onConnectionChange = useCallback(
    (payload: ConnectionChangePayload) => {
      logger('ZoomProvider', { userId: client.getCurrentUserInfo()?.userId, payload, trigger: 'onConnectionChange' })
      switch (payload.state) {
        case ConnectionState.Connected:
          setInSession(true)
          break
        case ConnectionState.Reconnecting:
          if (payload.reason === ReconnectReason.Failover) {
            hasFailedOver.current = true
            addZoomToast(ZoomToast.DISCONNECTED)
          }
          break
        case ConnectionState.Closed:
          if (payload.reason === 'ended by host') {
            onSessionClosedOrEndedCallback(false)
          } else {
            setInSession(false)
          }
          break
      }
    },
    [addZoomToast, client, logger, onSessionClosedOrEndedCallback],
  )

  const onCurrentAudioChange = useCallback(
    async (payload: {
      action: AudioChangeAction
      type?: 'phone' | 'computer'
      source?: MutedSource | LeaveAudioSource
    }) => {
      logger('ZoomProvider', {
        userId: client.getCurrentUserInfo()?.userId,
        payload,
        trigger: 'onCurrentAudioChange',
      })
      switch (payload.action) {
        case AudioChangeAction.Join:
          // Audio may already have joined unmuted if the mic check finished before startAudio ran
          const unmuteOnJoin = unmuteOnAudioJoinRef.current && !!client.getCurrentUserInfo()?.muted
          unmuteOnAudioJoinRef.current = false
          if (unmuteOnJoin) {
            try {
              await client.getMediaStream()?.unmuteAudio()
            } catch (error) {
              setMuted(true)
              trackEvent?.(ZOOM_EVENTS.TOGGLE_MUTE_ERROR, {
                [ZOOM_PROPERTIES.MUTED]: false,
                [ZOOM_PROPERTIES.ERROR]: getErrorObject(error),
              })
            }
          }
          setTimeout(() => {
            const sdkMuted = !!client.getCurrentUserInfo()?.muted
            if (sdkMuted) {
              trackMicMutedAutomatically('join_snapshot')
            }
            setMuted(sdkMuted)
          }, 1000)
          break
        case AudioChangeAction.Leave:
          micCheckPendingRef.current = false
          unmuteOnAudioJoinRef.current = false
          if (payload.source !== LeaveAudioSource.Active) {
            trackMicMutedAutomatically('audio_leave', payload.source)
          }
          setMuted(true)
          break
        case AudioChangeAction.Muted:
          if (payload.source !== MutedSource.Active) {
            trackMicMutedAutomatically('zoom_muted_event', payload.source)
          }
          setMuted(true)
          break
        case AudioChangeAction.Unmuted:
          setMuted(false)
          break
      }
    },
    [client, logger, setMuted, trackEvent, trackMicMutedAutomatically],
  )

  const onVideoCapturingChange = useCallback(
    async (payload: { state: VideoCapturingState }) => {
      logger('ZoomProvider', {
        userId: client.getCurrentUserInfo()?.userId,
        payload,
        trigger: 'onVideoCapturingChange',
      })
      switch (payload.state) {
        case VideoCapturingState.Started:
          setSessionVideoOff(false)
          break
        case VideoCapturingState.Stopped:
          setSessionVideoOff(true)
          break
      }
    },
    [client, logger, setSessionVideoOff],
  )

  const onDeviceChange = useCallback(async () => {
    ZoomVideo.getDevices()
      .then((devices) => {
        const videoInputs = devices
          .filter((device) => device.kind === 'videoinput')
          .map((device) => ({ deviceId: device.deviceId, deviceName: device.label }))
        setCameraList(videoInputs)
        if (videoInput && !videoInputs.find((input) => input.deviceId === videoInput)) {
          setVideoInput(null)
        }
        const audioInputs = devices.filter((device) => device.kind === 'audioinput')
        if (audioInput && !audioInputs.find((input) => input.deviceId === audioInput)) {
          setAudioInput(null)
        }

        const audioOutputs = devices.filter((device) => device.kind === 'audiooutput')
        if (audioOutput && !audioOutputs.find((input) => input.deviceId === audioOutput)) {
          setAudioOutput(null)
        }
      })
      .catch(() => {})
  }, [audioInput, audioOutput, videoInput])

  useEffect(() => {
    if (!attached || isMobileBrowser) {
      return
    }
    onDeviceChange()
    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange)
    return () => navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange)
  }, [attached, isMobileBrowser, onDeviceChange])

  const startAudio = useCallback(async () => {
    trackEvent?.(ZOOM_EVENTS.START_AUDIO, {
      [ZOOM_PROPERTIES.STREAM_EXISTS]: !!stream,
      [ZOOM_PROPERTIES.MUTED]: muted,
    })
    if (!stream) {
      return
    }

    try {
      await stream.startAudio({ mute: muted })
      if (audioInput) {
        await stream.switchMicrophone(audioInput)
      }

      if (audioOutput) {
        await stream.switchSpeaker(audioOutput)
      }

      trackEvent?.(ZOOM_EVENTS.START_AUDIO_SUCCESS, {
        [ZOOM_PROPERTIES.MUTED]: muted,
      })
    } catch (error) {
      trackEvent?.(ZOOM_EVENTS.START_AUDIO_ERROR, {
        [ZOOM_PROPERTIES.ERROR]: getErrorObject(error),
        [ZOOM_PROPERTIES.MUTED]: muted,
      })
      if (error.type === 'INSUFFICIENT_PRIVILEGES' && error.reason === 'USER_FORBIDDEN_MICROPHONE') {
        setShowPermissionsModal(true)
      }
    }
  }, [audioInput, audioOutput, muted, stream, trackEvent])

  const toggleMute = useCallback(async () => {
    trackEvent?.(ZOOM_EVENTS.TOGGLE_MUTE, {
      [ZOOM_PROPERTIES.STREAM_EXISTS]: !!stream,
      [ZOOM_PROPERTIES.MUTED]: muted,
      [ZOOM_PROPERTIES.SESSION_STARTED]: sessionStarted,
    })
    if (!sessionStarted) {
      if (muted) {
        try {
          micCheckPendingRef.current = true
          const micCheck = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
          if (micCheck && micCheckPendingRef.current) {
            micCheckPendingRef.current = false
            setMuted(false)
            // The session can start while the permission check is pending (it waits on
            // the browser prompt). Audio then starts with the earlier muted state, so
            // apply the unmute to Zoom too, now or once its audio has joined. The stream
            // is read fresh because Zoom may have connected after this toggle began.
            if (sessionStartedRef.current) {
              if (client.getCurrentUserInfo()?.audio) {
                try {
                  await client.getMediaStream()?.unmuteAudio()
                } catch (error) {
                  // Permission was already granted, so this is not a case for the permissions modal
                  setMuted(true)
                  trackEvent?.(ZOOM_EVENTS.TOGGLE_MUTE_ERROR, {
                    [ZOOM_PROPERTIES.MUTED]: muted,
                    [ZOOM_PROPERTIES.ERROR]: getErrorObject(error),
                  })
                  return
                }
              } else {
                unmuteOnAudioJoinRef.current = true
              }
            }

            trackEvent?.(ZOOM_EVENTS.TOGGLE_MUTE_SUCCESS, {
              [ZOOM_PROPERTIES.MUTED]: false,
              [ZOOM_PROPERTIES.SESSION_STARTED]: sessionStartedRef.current,
            })
          }
        } catch (error) {
          trackEvent?.(ZOOM_EVENTS.TOGGLE_MUTE_ERROR, {
            [ZOOM_PROPERTIES.MUTED]: muted,
            [ZOOM_PROPERTIES.ERROR]: getErrorObject(error),
          })
          setShowPermissionsModal(true)
        }
      } else {
        unmuteOnAudioJoinRef.current = false
        setMuted(true)
        trackEvent?.(ZOOM_EVENTS.TOGGLE_MUTE_SUCCESS, {
          [ZOOM_PROPERTIES.MUTED]: true,
          [ZOOM_PROPERTIES.SESSION_STARTED]: false,
        })
      }
      return
    }

    if (!stream) {
      return
    }

    micCheckPendingRef.current = false
    unmuteOnAudioJoinRef.current = false
    try {
      if (muted) {
        if (!client.getCurrentUserInfo()?.audio) {
          await startAudio()
        }
        await stream.unmuteAudio()
      } else {
        await stream.muteAudio()
      }
      trackEvent?.(ZOOM_EVENTS.TOGGLE_MUTE_SUCCESS, {
        [ZOOM_PROPERTIES.MUTED]: !muted,
        [ZOOM_PROPERTIES.SESSION_STARTED]: true,
      })
    } catch (error) {
      trackEvent?.(ZOOM_EVENTS.TOGGLE_MUTE_ERROR, {
        [ZOOM_PROPERTIES.MUTED]: muted,
        [ZOOM_PROPERTIES.ERROR]: getErrorObject(error),
      })
    }
  }, [trackEvent, stream, muted, sessionStarted, client, startAudio])

  const getShouldStopVideo = useCallback(
    (deviceId: string | null, elementToAttach: HTMLCanvasElement | HTMLVideoElement) => {
      if (localVideoOff) {
        return false
      }

      if (prevPreviewElement.current !== elementToAttach) {
        return true
      }

      if (videoInput !== deviceId) {
        return true
      }

      return false
    },
    [localVideoOff, videoInput],
  )

  const restartLocalVideo = useCallback(
    async (deviceId: string | null) => {
      const elementToAttach = previewVideoElement
      if (!elementToAttach) {
        return
      }

      const shouldStopVideo = getShouldStopVideo(deviceId, elementToAttach)
      if (shouldStopVideo) {
        try {
          await localVideoTrack.current?.stop()
        } catch {}
      }

      try {
        if (localVideoTrack.current) {
          ;(localVideoTrack.current as any).deviceId = deviceId
          await localVideoTrack.current.start(elementToAttach)
        } else {
          localVideoTrack.current = ZoomVideo.createLocalVideoTrack(deviceId ?? undefined)
          await localVideoTrack.current.start(elementToAttach)
        }
        prevPreviewElement.current = elementToAttach
      } catch {}
    },
    [getShouldStopVideo, previewVideoElement],
  )

  const startLocalVideo = useCallback(async () => {
    await restartLocalVideo(videoInput)
    setLocalVideoOff(false)
  }, [restartLocalVideo, videoInput])

  const stopLocalVideo = useCallback(async () => {
    try {
      await localVideoTrack.current?.stop()
    } catch {}
    setLocalVideoOff(true)
    prevPreviewElement.current = null
  }, [])

  // Frees the camera when its preview element goes away, without recording the
  // camera as off: localVideoOff is the user's choice, which the next preview
  // element and the session-start handoff both read to decide whether to start it.
  const releaseLocalVideo = useCallback(async () => {
    try {
      await localVideoTrack.current?.stop()
    } catch {}
    prevPreviewElement.current = null
  }, [])
  const startVideo = useCallback(
    async (selfElement: HTMLElement) => {
      if (!stream) {
        return
      }
      try {
        stream && (await stream.detachVideo(client.getCurrentUserInfo()?.userId))
      } catch (err) {
        Sentry.addBreadcrumb({
          category: 'Zoom',
          data: {
            videoInput,
            selfElement,
            prevSelfElement: prevSelfElement?.current,
            sessionInfo: client.getSessionInfo,
          },
        })
        if (!isIgnorableZoomError(err)) {
          Sentry.captureException(err)
        }
      }
      const urlForEffect = getVideoEffectURL(videoEffect)
      let virtualBackgroundRejected = false
      try {
        await stream.startVideo({
          cameraId: videoInput ?? undefined,
          mirrored: true,
          virtualBackground: urlForEffect
            ? {
                imageUrl: urlForEffect,
              }
            : undefined,
        })
      } catch (error) {
        // The camera matters more than the background, so start once more
        // without it when Zoom rejects or cannot load the background.
        if (!urlForEffect || !isVirtualBackgroundStartError(error)) {
          throw error
        }
        Sentry.addBreadcrumb({
          category: 'Zoom',
          message: 'Virtual background rejected by the SDK; starting the camera without it',
          data: { videoInput, videoEffect },
        })
        await stream.startVideo({
          cameraId: videoInput ?? undefined,
          mirrored: true,
          virtualBackground: undefined,
        })
        virtualBackgroundRejected = true
      }
      const element = await stream.attachVideo(
        client.getCurrentUserInfo()?.userId,
        stream.isSupportHDVideo() ? VideoQuality.Video_720P : VideoQuality.Video_360P,
      )
      if ('node-id' in element) {
        element.style.width = '100%'
        element.style.height = '100%'
        selfElement.appendChild(element)
      }
      prevSelfElement.current = selfElement
      if (virtualBackgroundRejected) {
        // Clear the persisted effect so later starts don't retry a background
        // this device cannot apply. Done after the attach settles so the
        // effect-change rerender cannot schedule a competing start.
        setVideoEffectEnum(VideoCallEffect.NONE)
        addZoomToast(ZoomToast.VIRTUAL_BACKGROUND_UNSUPPORTED)
      }
    },
    [addZoomToast, client, stream, videoEffect, videoInput],
  )

  const stopVideo = useCallback(async () => {
    if (!stream) {
      return
    }

    await stream.stopVideo()
    const detachedVideos = await stream.detachVideo(client.getCurrentUserInfo()?.userId)
    if (Array.isArray(detachedVideos)) {
      detachedVideos?.forEach((e) => e.remove())
    } else {
      detachedVideos?.remove()
    }
    prevSelfElement.current = null
  }, [client, stream])

  const toggleLocalVideo = useCallback(async () => {
    if (!previewVideoElement) {
      return
    }
    if (videoOff) {
      await startLocalVideo()
    } else {
      await stopLocalVideo()
    }
  }, [previewVideoElement, startLocalVideo, stopLocalVideo, videoOff])

  const toggleVideo = useCallback(async () => {
    trackEvent?.(ZOOM_EVENTS.TOGGLE_VIDEO, {
      [ZOOM_PROPERTIES.STREAM_EXISTS]: !!stream,
      [ZOOM_PROPERTIES.VIDEO_OFF]: videoOff,
      [ZOOM_PROPERTIES.SESSION_STARTED]: sessionStarted,
    })
    if (!sessionStarted) {
      try {
        await toggleLocalVideo()
        trackEvent?.(ZOOM_EVENTS.TOGGLE_VIDEO_SUCCESS, {
          [ZOOM_PROPERTIES.VIDEO_OFF]: !videoOff,
          [ZOOM_PROPERTIES.SESSION_STARTED]: sessionStarted,
        })
      } catch (error) {
        trackEvent?.(ZOOM_EVENTS.TOGGLE_VIDEO_ERROR, {
          [ZOOM_PROPERTIES.ERROR]: getErrorObject(error),
          [ZOOM_PROPERTIES.VIDEO_OFF]: videoOff,
          [ZOOM_PROPERTIES.SESSION_STARTED]: sessionStarted,
        })
        setShowPermissionsModal(true)
      }
      return
    }
    if (!stream || !selfElement) {
      return
    }
    try {
      if (videoOff) {
        await startVideo(selfElement)
      } else {
        await stopVideo()
      }
      trackEvent?.(ZOOM_EVENTS.TOGGLE_VIDEO_SUCCESS, {
        [ZOOM_PROPERTIES.VIDEO_OFF]: !videoOff,
        [ZOOM_PROPERTIES.SESSION_STARTED]: sessionStarted,
      })
    } catch (error) {
      trackEvent?.(ZOOM_EVENTS.TOGGLE_VIDEO_ERROR, {
        [ZOOM_PROPERTIES.ERROR]: getErrorObject(error),
        [ZOOM_PROPERTIES.VIDEO_OFF]: videoOff,
        [ZOOM_PROPERTIES.SESSION_STARTED]: sessionStarted,
      })
      Sentry.addBreadcrumb({
        category: 'Zoom',
        data: {
          selfElement,
          videoOff,
          sessionStarted,
          error,
          stream,
          prevSelfElement: prevSelfElement?.current,
        },
      })
      if (error.type === 'VIDEO_USER_FORBIDDEN_CAPTURE') {
        setShowPermissionsModal(true)
      }
      if (error.type === 'INVALID_OPERATION' && error.reason === 'camera is closed') {
        try {
          await stream.startVideo()
        } catch {
          Sentry.captureException(error)
        }
      }

      if (!isIgnorableZoomError(error)) {
        Sentry.captureException(error)
      }
    }
  }, [trackEvent, stream, videoOff, sessionStarted, selfElement, toggleLocalVideo, startVideo, stopVideo])

  useEffect(() => {
    async function switchVideo() {
      if (
        (sessionVideoOff && localVideoOff) ||
        !sessionStarted ||
        !selfElement ||
        selfElement.isEqualNode(prevSelfElement.current)
      ) {
        if (!selfElement && prevSelfElement.current) {
          await stopVideo()
        }
        return
      }

      if (prevSelfElement.current) {
        await stopVideo().catch((err) => {
          Sentry.addBreadcrumb({
            category: 'Zoom',
            data: {
              sessionStarted,
              sessionVideoOff,
              localVideoOff,
              selfElement,
              prevSelfElement: prevSelfElement?.current,
              videoOff,
              sessionInfo: client.getSessionInfo,
            },
          })
          Sentry.captureException(err)
        })
      }
      // The session now owns the camera. Clear the waiting-room preview's choice so a
      // later in-session camera-off (sessionVideoOff) is not restarted by this effect.
      setLocalVideoOff(true)
      setTimeout(
        () =>
          startVideo(selfElement).catch((err) => {
            Sentry.addBreadcrumb({
              category: 'Zoom',
              data: {
                sessionStarted,
                sessionVideoOff,
                localVideoOff,
                selfElement,
                prevSelfElement: prevSelfElement?.current,
                videoOff,
                sessionInfo: client.getSessionInfo,
              },
            })
            Sentry.captureException(err)
          }),
        1000,
      )
    }
    switchVideo().catch(() => {})
  }, [
    client.getSessionInfo,
    localVideoOff,
    selfElement,
    sessionStarted,
    sessionVideoOff,
    startVideo,
    stopVideo,
    videoOff,
  ])

  useEffect(() => {
    let cancelled = false
    async function switchLocalVideo() {
      if (
        (sessionVideoOff && localVideoOff) ||
        sessionStarted ||
        !previewVideoElement ||
        previewVideoElement === prevPreviewElement.current
      ) {
        if (!previewVideoElement && prevPreviewElement.current) {
          // If the registered video element is being swapped to a sibling (e.g.
          // banner → full-screen self-view on maximize) the unmount from the banner can
          // cause the video to be stopped before the new element is mounted. This
          // timeout avoids restarting the camera when the new element mounts in time; if
          // it mounts later, the camera is restarted on it.
          await new Promise((r) => setTimeout(r, 0))
          if (!cancelled) {
            await releaseLocalVideo()
          }
        }
        return
      }

      await restartLocalVideo(videoInput)
      setLocalVideoOff(false)
    }

    switchLocalVideo().catch(() => {})
    return () => {
      cancelled = true
    }
  }, [
    localVideoOff,
    previewVideoElement,
    releaseLocalVideo,
    restartLocalVideo,
    sessionStarted,
    sessionVideoOff,
    startLocalVideo,
    videoEffect,
    videoInput,
    videoOff,
  ])

  const getNextCameraDevice = useCallback(() => {
    if (!stream || cameraList.length === 0) {
      return null
    }

    const activeCamera = stream.getActiveCamera()
    if (!activeCamera) {
      return cameraList[0].deviceId
    }

    if (activeCamera === 'default') {
      return cameraList.length > 1 ? cameraList[1].deviceId : cameraList[0].deviceId
    }
    const currCameraIdx = cameraList.findIndex((c) => c.deviceId === activeCamera)
    if (currCameraIdx === -1) {
      return cameraList[0].deviceId
    }

    return cameraList[(currCameraIdx + 1) % cameraList.length].deviceId
  }, [cameraList, stream])

  const setVideoEffect = useCallback(
    async (effect: VideoCallEffect) => {
      if (!stream) {
        return
      }

      const url = getVideoEffectURL(effect)
      await stream.updateVirtualBackgroundImage(url)
      setVideoEffectEnum(effect)
    },
    [stream],
  )

  const switchCameraAndEffectLocal = useCallback(
    async (deviceId: string | null) => {
      await restartLocalVideo(deviceId)
      setVideoInput(deviceId)
    },
    [restartLocalVideo],
  )

  const switchCameraAndVideoEffect = useCallback(
    async (deviceId: string | null = null, videoEffect: VideoCallEffect) => {
      if (videoOff) {
        setVideoInput(deviceId)
        setVideoEffectEnum(videoEffect)
        return
      }

      if (!sessionStarted) {
        if (deviceId !== videoInput) {
          await switchCameraAndEffectLocal(deviceId)
        }
        setVideoEffectEnum(videoEffect)
        return
      }
      if (!stream) {
        return
      }

      if (!deviceId) {
        deviceId = getNextCameraDevice()
      }

      if (!deviceId) {
        return
      }
      await stream.switchCamera(deviceId)
      setVideoInput(deviceId)
      await setVideoEffect(videoEffect)
    },
    [videoOff, sessionStarted, stream, setVideoEffect, videoInput, switchCameraAndEffectLocal, getNextCameraDevice],
  )

  const stopAllVideo = useCallback(async () => {
    try {
      await localVideoTrack.current?.stop()
    } catch {}
    try {
      await client.getMediaStream()?.stopVideo()
    } catch {}

    setLocalVideoOff(true)
    setSessionVideoOff(true)
  }, [client])

  useEffect(() => {
    client.init('en-US', 'Global', {
      patchJsMedia: true,
      leaveOnPageUnload: true,
      stayAwake: true,
      enforceVirtualBackground: browserSupportsVirtualBackground,
    })
    return () => {
      const destroyClient = async () => {
        await stopAllVideo()
        ZoomVideo.destroyClient()
      }
      destroyClient().catch(() => {})
    }
  }, [browserSupportsVirtualBackground, client, stopAllVideo])

  const joinSession = useCallback(
    (params: JoinSessionParams) =>
      client
        .join(params.sessionName, params.token, params.userName, params.sessionPassword)
        .then(() => setJoinTime(new Date()))
        .catch(() => {}),
    [client],
  )

  const leaveSession = useCallback(
    async (params: LeaveSessionParams | undefined) => {
      const end = params?.end ?? false
      const rejoining = params?.rejoining ?? false
      await stopAllVideo()
      if (!inSession) {
        sessionClosedCallback?.()
        return
      }
      if (end) {
        try {
          client
            .getCommandClient()
            .send(ZoomCommand.END_SESSION)
            .then(() => {
              setTimeout(() => {
                client
                  .leave(true)
                  .then(() => {
                    onSessionClosedOrEndedCallback(rejoining)
                  })
                  .catch(() => {})
              }, LEAVE_DELAY_ON_END_COMMAND_SEND_MS)
            })
            .catch(() => {})
        } catch (err) {
          Sentry.addBreadcrumb({
            category: 'Zoom',
            data: {
              inSession,
              client,
              end,
              rejoining,
            },
          })
          Sentry.captureException(err)
        }
      } else {
        client
          .leave(false)
          .then(() => {
            onSessionClosedOrEndedCallback(rejoining)
          })
          .catch(() => {})
      }
    },
    [client, inSession, onSessionClosedOrEndedCallback, sessionClosedCallback, stopAllVideo],
  )
  const onUsersChange = useCallback(() => {
    const users = client.getAllUser()
    const self = client.getCurrentUserInfo()
    setRemoteParticipants((prevRemoteParticipants) => {
      const prevMap = Object.fromEntries(
        prevRemoteParticipants.map((prevParticipant) => [prevParticipant.userId, prevParticipant]),
      )

      return users
        .filter((user) => user.userId !== self?.userId)
        .map((user) => ({
          userId: user.userId,
          isVideoOn: user.bVideoOn,
          isMuted: user.muted ?? true,
          joinTime: user.userId in prevMap ? prevMap[user.userId].joinTime : new Date(),
          userIdentity: user.userIdentity,
        }))
    })
  }, [client, setRemoteParticipants])

  const stopScreenshare = useCallback(() => {
    if (!stream) {
      return
    }

    stream
      .stopShareScreen()
      .then(() => {
        setSharingUserId(null)
        stream.stopShareView()
        trackEvent?.(ZOOM_EVENTS.STOP_SCREENSHARE)
      })
      .catch(() => {})
  }, [stream, trackEvent])

  const startScreenshare = useCallback(() => {
    if (!stream || !screenshareElement || screenshareElement instanceof HTMLCanvasElement) {
      return
    }

    stream
      .startShareScreen(screenshareElement, {
        controls: { monitorTypeSurfaces: 'exclude', selfBrowserSurface: 'include' },
      })
      .then(() => {
        trackEvent?.(ZOOM_EVENTS.START_SCREENSHARE)
        setSharingUserId(client.getCurrentUserInfo()?.userId)
        if (screenshareElement.srcObject instanceof MediaStream) {
          try {
            if (screenshareElement.srcObject.getVideoTracks()[0].getSettings()?.displaySurface !== 'browser') {
              addZoomToast(ZoomToast.SCREENSHARE)
              stopScreenshare()
            }
          } catch (error) {
            // Ignore NotAllowedError when page is not visible
            if (error instanceof DOMException && error.name !== 'NotAllowedError') {
              throw error
            }
          }
        }
      })
      .catch(() => {})
  }, [stream, screenshareElement, trackEvent, client, addZoomToast, stopScreenshare])

  const onPeerShareStateChange = useCallback((payload: { userId: number; action: 'Start' | 'Stop' }) => {
    setSharingUserId(payload.action === 'Start' ? payload.userId : null)
  }, [])

  const onPassivelyStopShare = useCallback(() => {
    setSharingUserId(null)
    stream?.stopShareView()
    trackEvent?.(ZOOM_EVENTS.STOP_SCREENSHARE)
  }, [stream, trackEvent])

  const notifyRecordingStatusChange = useCallback(
    (isRecording: boolean | undefined) => {
      if (isRecording === undefined) {
        return
      }

      if (isRecording) {
        client
          .getCommandClient()
          .send(ZoomCommand.SEND_RECORDING_STATUS_ON)
          .catch(() => {})
      } else {
        client
          .getCommandClient()
          .send(ZoomCommand.SEND_RECORDING_STATUS_OFF)
          .catch(() => {})
      }
    },
    [client],
  )
  const onCommandChannelMessage = useCallback(
    ({ text: command }: { text: ZoomCommand }) => {
      switch (command) {
        case ZoomCommand.START_SESSION:
          updateSessionStarted(true)
          break
        case ZoomCommand.FETCH_RECORDING_STATUS:
          notifyRecordingStatusChange(isRecording)
          break
        case ZoomCommand.SEND_RECORDING_STATUS_ON:
        case ZoomCommand.SEND_RECORDING_STATUS_OFF:
          if (config.recordingFetchMode === RecordingFetchMode.PROVIDER) {
            return
          }
          if (isRecording !== undefined) {
            addZoomToast(
              command === ZoomCommand.SEND_RECORDING_STATUS_ON
                ? ZoomToast.RECORDING_STARTED
                : ZoomToast.RECORDING_STOPPED,
            )
          }
          setIsRecording(command === ZoomCommand.SEND_RECORDING_STATUS_ON)
          break
      }
    },
    [
      notifyRecordingStatusChange,
      isRecording,
      config.recordingFetchMode,
      addZoomToast,
      setIsRecording,
      updateSessionStarted,
    ],
  )

  const startSession = useCallback(() => {
    client
      .getCommandClient()
      .send(ZoomCommand.START_SESSION)
      .then(() => {
        updateSessionStarted(true)
      })
      .catch(() => {})
  }, [client, updateSessionStarted])

  const remoteParticipant = useMemo(
    () => (remoteParticipants.length > 0 ? remoteParticipants[0] : null),
    [remoteParticipants],
  )

  useEffect(() => {
    async function handleParticipantChange() {
      if (!participantElement || !stream) {
        return
      }

      if (prevParticipantElement.current && currentDisplayedParticipant.current) {
        const detachedVideos = await stream.detachVideo(currentDisplayedParticipant.current)
        if (Array.isArray(detachedVideos)) {
          detachedVideos?.forEach((e) => e.remove())
        } else {
          detachedVideos?.remove()
        }
        currentDisplayedParticipant.current = null
        prevParticipantElement.current = null
      }
      if (remoteParticipant?.isVideoOn) {
        const element = await stream.attachVideo(
          remoteParticipant.userId as number,
          stream.isSupportHDVideo() && !isMobileBrowser ? VideoQuality.Video_720P : VideoQuality.Video_360P,
        )
        if ('node-id' in element) {
          element.style.width = '100%'
          element.style.height = '100%'
          participantElement.appendChild(element)
        }
        currentDisplayedParticipant.current = remoteParticipant.userId as number
        prevParticipantElement.current = participantElement
      }
    }
    handleParticipantChange().catch(() => {})
  }, [stream, remoteParticipant?.isVideoOn, remoteParticipant?.userId, participantElement, isMobileBrowser])

  useEffect(() => {
    if (!videoInput) {
      if (isMobileBrowser) {
        setVideoInput(MobileVideoFacingMode.User)
      } else if (cameraList.length > 0) {
        setVideoInput(cameraList[0].deviceId)
      }
    }
  }, [cameraList, isMobileBrowser, videoInput])

  const switchMicrophone = useCallback(
    (deviceId: string) => {
      if (!sessionStarted) {
        setAudioInput(deviceId)
        return
      }

      if (!stream) {
        return
      }
      stream
        .switchMicrophone(deviceId)
        .then(() => setAudioInput(deviceId))
        .catch(() => {})
    },
    [sessionStarted, stream],
  )

  const switchSpeaker = useCallback(
    (deviceId: string) => {
      if (!sessionStarted) {
        setAudioOutput(deviceId)
        return
      }

      if (!stream) {
        return
      }
      stream
        .switchSpeaker(deviceId)
        .then(() => setAudioOutput(deviceId))
        .catch(() => {})
    },
    [sessionStarted, stream],
  )

  const updateSettings = useCallback(
    (settings: DeviceSettings) => {
      if (settings.videoInput && (settings.videoInput !== videoInput || settings.videoEffect !== videoEffect)) {
        switchCameraAndVideoEffect(settings.videoInput, settings.videoEffect ?? VideoCallEffect.NONE)
      }

      if (settings.audioInput && settings.audioInput !== audioInput) {
        switchMicrophone(settings.audioInput)
      }

      if (settings.audioOutput && settings.audioOutput !== audioOutput) {
        switchSpeaker(settings.audioOutput)
      }
    },
    [audioInput, audioOutput, switchCameraAndVideoEffect, switchMicrophone, switchSpeaker, videoEffect, videoInput],
  )

  const onActiveShareChange = useCallback(
    async (payload: { state: 'Active' | 'Inactive'; userId: number }) => {
      trackEvent?.(payload.state === 'Active' ? ZOOM_EVENTS.RECEIVE_SCREENSHARE : ZOOM_EVENTS.STOP_RECEIVE_SCREENSHARE)
      if (!participantScreenshareElement || !stream) {
        return
      }
      if (payload.state === 'Active') {
        try {
          stream.startShareView(participantScreenshareElement, payload.userId)
        } catch (err) {
          Sentry.captureException(err)
        }
        trackEvent?.(ZOOM_EVENTS.RENDER_SCREENSHARE)
      } else {
        stream.stopShareView()
        trackEvent?.(ZOOM_EVENTS.STOP_RENDER_SCREENSHARE)
      }
    },
    [participantScreenshareElement, stream, trackEvent],
  )

  useEffect(() => localStorage.setItem(IO_DEVICES.CAMERA, videoInput || ''), [videoInput])
  useEffect(() => localStorage.setItem(IO_DEVICES.MICROPHONE, audioInput || ''), [audioInput])
  useEffect(() => localStorage.setItem(IO_DEVICES.SPEAKER, audioOutput || ''), [audioOutput])
  useEffect(() => localStorage.setItem(IO_DEVICES.EFFECT, videoEffect), [videoEffect])

  const onNetworkQualityChange = useCallback(
    (payload: { userId: number; type: 'uplink' | 'downlink'; level: number }) => {
      const isSelf = payload.userId === client.getCurrentUserInfo()?.userId
      if (payload.level <= 2) {
        trackEvent?.(ZOOM_EVENTS.BAD_CONNECTION, {
          [ZOOM_PROPERTIES.IS_SELF]: isSelf,
          [ZOOM_PROPERTIES.LEVEL]: payload.level,
          [ZOOM_PROPERTIES.CONNECTION_TYPE]: payload.type,
        })
      }
      if (isSelf && payload.type === 'uplink') {
        const quality = NETWORK_QUALITY_MAP[payload.level]
        setNetworkQuality(quality)
        if (quality === ZoomNetworkQuality.BAD) {
          addZoomToast(ZoomToast.BAD_CONNECTION)
        }
      }
    },
    [addZoomToast, client, setNetworkQuality, trackEvent],
  )

  useEffect(() => {
    client.on('connection-change', onConnectionChange)
    client.on('current-audio-change', onCurrentAudioChange)
    client.on('video-capturing-change', onVideoCapturingChange)
    client.on('user-added', onUsersChange)
    client.on('user-removed', onUsersChange)
    client.on('user-updated', onUsersChange)
    client.on('peer-share-state-change', onPeerShareStateChange)
    client.on('passively-stop-share', onPassivelyStopShare)
    client.on('command-channel-message', onCommandChannelMessage)
    client.on('active-share-change', onActiveShareChange)
    client.on('network-quality-change', onNetworkQualityChange)
    return () => {
      client.off('connection-change', onConnectionChange)
      client.off('current-audio-change', onCurrentAudioChange)
      client.off('video-capturing-change', onVideoCapturingChange)
      client.off('user-added', onUsersChange)
      client.off('user-removed', onUsersChange)
      client.off('user-updated', onUsersChange)
      client.off('peer-share-state-change', onPeerShareStateChange)
      client.off('passively-stop-share', onPassivelyStopShare)
      client.off('command-channel-message', onCommandChannelMessage)
      client.off('active-share-change', onActiveShareChange)
      client.off('network-quality-change', onNetworkQualityChange)
    }
  }, [
    client,
    onConnectionChange,
    onCurrentAudioChange,
    onVideoCapturingChange,
    onUsersChange,
    onPeerShareStateChange,
    onPassivelyStopShare,
    onCommandChannelMessage,
    onActiveShareChange,
    onNetworkQualityChange,
  ])

  const settings = useMemo<DeviceSettings>(
    () => ({
      audioInput: audioInput ?? undefined,
      audioOutput: audioOutput ?? undefined,
      videoInput: videoInput ?? undefined,
      videoEffect,
    }),
    [audioInput, audioOutput, videoEffect, videoInput],
  )

  const fetchRecordingStatus = useCallback(async () => {
    await client
      .getCommandClient()
      .send(ZoomCommand.FETCH_RECORDING_STATUS)
      .catch(() => {})
  }, [client])

  return useMemo(
    () => ({
      muted,
      toggleMute,
      videoOff,
      toggleVideo,
      cameraList,
      userId: client.getCurrentUserInfo()?.userId,
      joinSession,
      remoteParticipants,
      leaveSession,
      inSession,
      setSelfElement,
      sharingUserId,
      startScreenshare,
      stopScreenshare,
      setScreenshareElement,
      videoEffect,
      setVideoEffect,
      sessionStarted,
      startSession,
      setSessionStarted: updateSessionStarted,
      startAudio,
      setParticipantElement,
      setPreviewVideoElement,
      setPreviewCanvasElement: () => {},
      isPreviewVBMode: false,
      updateSettings,
      settings,
      setParticipantScreenshareElement,
      stream,
      setCameraRef: () => {},
      joinTime,
      userIdentity: client.getCurrentUserInfo()?.userIdentity,
      supportsVirtualBackground,
      showPermissionsModal,
      closePermissionsModal: () => setShowPermissionsModal(false),
      requestRecordingStatus: fetchRecordingStatus,
      notifyRecordingStatusChange,
    }),
    [
      muted,
      toggleMute,
      videoOff,
      toggleVideo,
      cameraList,
      client,
      joinSession,
      remoteParticipants,
      leaveSession,
      inSession,
      sharingUserId,
      startScreenshare,
      stopScreenshare,
      videoEffect,
      setVideoEffect,
      sessionStarted,
      startSession,
      startAudio,
      updateSettings,
      settings,
      stream,
      joinTime,
      supportsVirtualBackground,
      showPermissionsModal,
      fetchRecordingStatus,
      notifyRecordingStatusChange,
    ],
  )
}
